import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useLive } from "@/contexts/LiveContext";
import MiniFlip from "@/components/MiniFlip";
import TheFlip from "@/components/TheFlip";
import DebateCard from "@/components/DebateCard";
import TrendingTicker from "@/components/TrendingTicker";
import StreakBanner from "@/components/StreakBanner";
import { getCategoryMeta } from "@/lib/categories";
import { getAnonId, getVote, setVote } from "@/lib/voteStore";
import { guestWallReached, recordGuestVote, guestNudge, openAuth, openAuthWall } from "@/lib/guestGate";
import { VoteBurst, FloatingPlusOne, haptic } from "@/components/VoteFX";
import { Search, Flame, Swords, X, Sparkles, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Reality TV & Pop Culture", "Bollywood & OTT", "Cricket & Sports", "Influencers & Internet", "Dating & Gen Z", "Tech & Culture", "Money & Life", "Politics"];

function SpotlightCard({ topic, onVoted }) {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [picked, setPicked] = useState(() => (topic ? getVote(topic.topic_id) : null));
  const [voting, setVoting] = useState(false);
  const [voteAnim, setVoteAnim] = useState(0);
  const [kickSide, setKickSide] = useState(null);
  if (!topic) return null;
  const total = topic.votes_a + topic.votes_b;
  const pctA = total > 0 ? Math.round((topic.votes_a / total) * 100) : 50;
  const pctYour = picked === "a" ? pctA : 100 - pctA;
  const img = topic.theme_image?.url;

  const vote = async (e, side) => {
    e.stopPropagation();
    if (picked || voting) return;
    if (!user && guestWallReached(topic.topic_id)) { openAuthWall(); return; }
    setKickSide(side);
    haptic(26);
    setVoting(true);
    try {
      const { data } = await api.post("/votes", { topic_id: topic.topic_id, side, anon_id: getAnonId() });
      setPicked(side);
      setVote(topic.topic_id, side);
      setVoteAnim((v) => v + 1);
      onVoted?.(topic.topic_id, data.votes_a, data.votes_b);
      if (!user) {
        const n = guestNudge(recordGuestVote(topic.topic_id));
        toast(n.msg, n.action ? { action: { label: "Sign up", onClick: openAuth } } : undefined);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    } finally {
      setVoting(false);
    }
  };

  const vbtn = (side) => {
    const isA = side === "a";
    const active = picked === side;
    const dim = picked && !active;
    return (
      <motion.button
        onClick={(e) => vote(e, side)}
        disabled={!!picked || voting}
        whileTap={{ scale: 0.96 }}
        animate={dim ? { opacity: 0.4 } : { opacity: 1 }}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-bold rounded-xl border-2 transition-colors ${
          active
            ? isA ? "bg-view-a border-view-a text-white" : "bg-view-b border-view-b text-white"
            : isA ? "border-view-a/60 text-white bg-view-a/30 hover:bg-view-a"
                  : "border-view-b/70 text-white bg-view-b/25 hover:bg-view-b hover:text-black"
        }`}
        data-testid={`dotd-vote-${side}`}
      >
        {active && <Check className="w-4 h-4" />}
        <span className="truncate">{isA ? topic.side_a_label : topic.side_b_label}</span>
      </motion.button>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
      <div
        role="link" tabIndex={0}
        onClick={() => navigate(`/issue/${topic.topic_id}`)}
        onKeyDown={(e) => e.key === "Enter" && navigate(`/issue/${topic.topic_id}`)}
        className="block relative overflow-hidden rounded-2xl shadow-card-hover group cursor-pointer"
        style={{ background: "linear-gradient(120deg, #0D0F17 0%, #161923 55%, #7B2CBF 130%)" }}
        data-testid="debate-of-the-day-card"
      >
        {img && (
          <div className="absolute inset-0">
            <img src={img} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover opacity-25 group-hover:opacity-30 transition-opacity"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, #0D0F17 10%, rgba(22,25,35,0.85) 55%, rgba(123,44,191,0.65) 130%)" }} />
          </div>
        )}
        <motion.div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-view-b/20 blur-3xl -mr-16 -mt-16"
          animate={{ scale: [1, 1.25, 1], opacity: [0.6, 1, 0.6] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
        <div className="relative p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-3">
            <span className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-amber-300 bg-amber-300/10 px-2.5 py-1 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> Debate of the Day
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase" style={{ color: getCategoryMeta(topic.category).color }}>
              {(() => { const I = getCategoryMeta(topic.category).Icon; return <I className="w-3.5 h-3.5" strokeWidth={2.4} />; })()}
              {topic.category}
            </span>
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-white leading-tight mb-5">{topic.question}</h3>

          <div className="relative flex items-center gap-2.5 mb-3" onClick={(e) => e.stopPropagation()}>
            {vbtn("a")}
            <span className="text-[10px] text-white/40 font-bold shrink-0">VS</span>
            {vbtn("b")}
            <VoteBurst trigger={voteAnim} color={picked === "b" ? "#FFC300" : "#7B2CBF"} />
            <FloatingPlusOne trigger={voteAnim} color={picked === "b" ? "#FFC300" : "#7B2CBF"} />
          </div>

          {!picked ? (
            <motion.div className="flex items-center justify-center gap-1.5 text-[12px] font-semibold text-amber-300"
              animate={{ opacity: [0.55, 1, 0.55] }} transition={{ duration: 1.8, repeat: Infinity }} data-testid="dotd-nudge">
              👆 Tap a side to vote — reveal what India thinks
            </motion.div>
          ) : (
            <div data-testid="dotd-reveal">
              <div className="flex justify-center mb-1">
                <TheFlip votesA={topic.votes_a} votesB={topic.votes_b} voteAnim={voteAnim} kickSide={kickSide}
                  userSide={picked} labelA={topic.side_a_label} labelB={topic.side_b_label} compact />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white">
                  {pctYour < 50 ? `You're in the ${pctYour}% minority 👀` : `You're with the ${pctYour}% majority`}
                </span>
                <span className="shrink-0 flex items-center gap-1 text-xs font-semibold text-amber-300">
                  {pctYour < 50 ? "Defend your side" : "Join the debate"} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function TrendingSidebar({ trending }) {
  if (!trending) return null;
  const list = trending.trending || [];
  const autoScroll = list.length >= 3;
  const rows = autoScroll ? [...list, ...list] : list;
  return (
    <div className="space-y-6">
      {list.length > 0 && (
        <div className="bg-white rounded-2xl shadow-card p-5">
          <h3 className="flex items-center gap-2 font-heading text-lg text-dark mb-3">
            <Flame className="w-4 h-4 text-view-b" /> TRENDING
            <span className="ml-auto flex items-center gap-1 text-[10px] font-body font-semibold text-success">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> LIVE
            </span>
          </h3>
          <div className={`vscroll relative overflow-hidden ${autoScroll ? "h-52" : ""}`} data-testid="trending-sidebar-scroll">
            <div className={`space-y-3 ${autoScroll ? "vscroll-track" : ""}`} style={autoScroll ? { animationDuration: `${list.length * 4}s` } : undefined}>
              {rows.map((t, i) => (
                <Link key={`${t.topic_id}-${i}`} to={`/issue/${t.topic_id}`} className="flex items-start gap-3 group" data-testid={i < list.length ? `trending-${i}` : undefined}>
                  <span className="text-xs font-bold text-neutral mt-0.5">{(i % list.length) + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark leading-snug group-hover:text-view-a transition-colors line-clamp-2">{t.question}</p>
                    <span className="text-[11px] text-neutral">{t.total_votes} votes</span>
                  </div>
                </Link>
              ))}
            </div>
            {autoScroll && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent ticker-fade" />}
          </div>
        </div>
      )}

      {trending.most_contested?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-card p-5">
          <h3 className="flex items-center gap-2 font-heading text-lg text-dark mb-3">
            <Swords className="w-4 h-4 text-purple-500" /> MOST DIVIDED
          </h3>
          <div className="space-y-3">
            {trending.most_contested.slice(0, 3).map((t) => (
              <Link key={t.topic_id} to={`/issue/${t.topic_id}`} className="block group">
                <p className="text-sm font-medium text-dark leading-snug group-hover:text-view-a transition-colors line-clamp-2">{t.question}</p>
                <div className="mt-1.5"><MiniFlip votesA={t.votes_a} votesB={t.votes_b} /></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const { api } = useAuth();
  const { subscribe } = useLive();
  const [topics, setTopics] = useState([]);
  const [trending, setTrending] = useState(null);
  const [dotd, setDotd] = useState(null);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const searchTimeout = useRef(null);

  const fetchTopics = useCallback(async () => {
    try {
      const params = category !== "All" ? { category } : {};
      const { data } = await api.get("/topics", { params });
      setTopics(data);
    } catch (err) {
      console.error("Failed to fetch topics:", err);
    } finally {
      setLoading(false);
    }
  }, [api, category]);

  const fetchTrending = useCallback(async () => {
    try { const { data } = await api.get("/trending"); setTrending(data); } catch {}
  }, [api]);

  const fetchDotd = useCallback(async () => {
    try { const { data } = await api.get("/debate-of-the-day"); setDotd(data); } catch {}
  }, [api]);

  useEffect(() => { fetchTopics(); }, [fetchTopics]);
  useEffect(() => { fetchTrending(); }, [fetchTrending]);
  useEffect(() => { fetchDotd(); }, [fetchDotd]);

  const applyVotes = useCallback((topicId, va, vb) => {
    const patch = (t) => (t.topic_id === topicId ? { ...t, votes_a: va, votes_b: vb, total_votes: va + vb } : t);
    setTopics((prev) => prev.map(patch));
    setSearchResults((prev) => (prev ? prev.map(patch) : prev));
    setDotd((prev) => (prev ? patch(prev) : prev));
    setTrending((prev) => prev ? {
      trending: prev.trending?.map(patch),
      most_contested: prev.most_contested?.map(patch),
      most_discussed: prev.most_discussed?.map(patch),
    } : prev);
  }, []);

  useEffect(() => subscribe((msg) => {
    if (msg.type === "vote_update") applyVotes(msg.topic_id, msg.votes_a, msg.votes_b);
  }), [subscribe, applyVotes]);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/topics/search", { params: { q: searchQuery } });
        setSearchResults(data);
      } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, api]);

  const displayTopics = searchResults !== null ? searchResults : topics;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6" data-testid="home-page">
      <div className="text-center mb-6">
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-dark leading-none">
          EVERY ISSUE HAS <span className="shimmer-text">TWO SIDES</span>
        </h1>
        <p className="mt-2 text-neutral text-sm sm:text-base max-w-md mx-auto">
          Read both perspectives. Make your call. Watch the needle move.
        </p>
      </div>

      <TrendingTicker items={trending?.trending} />
      <StreakBanner />

      <div className="relative max-w-lg mx-auto mb-6">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral" />
        <input
          type="text"
          placeholder="Search debates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-a/30 focus:border-view-a transition-all"
          data-testid="search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral hover:text-dark" data-testid="search-clear-btn">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 no-scrollbar" data-testid="category-filter">
        {CATEGORIES.map((cat) => {
          const { Icon, color } = getCategoryMeta(cat);
          const active = category === cat;
          return (
            <button
              key={cat}
              onClick={() => { setCategory(cat); setSearchQuery(""); setSearchResults(null); }}
              className={`whitespace-nowrap flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full transition-all border ${
                active ? "text-white border-transparent shadow-sm" : "bg-white text-neutral hover:text-dark border-gray-200 hover:border-gray-300"
              }`}
              style={active ? { backgroundColor: color } : undefined}
              data-testid={`category-${cat.toLowerCase()}`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.4} style={active ? undefined : { color }} />
              {cat}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-8 space-y-4" data-testid="debate-feed">
          {!searchQuery && category === "All" && !loading && <SpotlightCard topic={dotd} onVoted={applyVotes} />}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-card p-6 animate-pulse">
                  <div className="h-3 bg-gray-100 rounded w-20 mb-4" />
                  <div className="h-6 bg-gray-100 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-gray-100 rounded w-1/2 mb-4" />
                  <div className="h-2 bg-gray-100 rounded w-full" />
                </div>
              ))}
            </div>
          ) : displayTopics.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral text-sm">
                {searchQuery ? `No debates found for "${searchQuery}"` : "No debates yet in this category."}
              </p>
            </div>
          ) : (
            displayTopics.map((topic, i) => (
              <DebateCard key={topic.topic_id} topic={topic} index={i} onVoted={applyVotes} />
            ))
          )}
        </div>

        <div className="hidden lg:block lg:col-span-4 space-y-6" data-testid="sidebar">
          <TrendingSidebar trending={trending} />
        </div>
      </div>
    </div>
  );
}
